/**
 * 画布连线层：Board 与管理后台只读预览共用。
 *
 * 锚点算法（右边缘中点 → 左边缘中点 + 贝塞尔）与颜色语义只在这一处维护，
 * 避免后台预览再实现一套导致视觉漂移。
 */
import type { MouseEvent as ReactMouseEvent } from 'react';
import { isAgentLineageEdge } from './connectionRules';
import type { CanvasEdge, CanvasNode } from './types';

interface CanvasEdgeLayerProps {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  selectedEdgeIds: string[];
  highlightedNodeIds: string[];
  /** false 时整层不接收指针事件（只读预览） */
  interactive?: boolean;
  onEdgeClick?: (edge: CanvasEdge, event: ReactMouseEvent<SVGPathElement>) => void;
}

export function CanvasEdgeLayer({
  nodes,
  edges,
  selectedEdgeIds,
  highlightedNodeIds,
  interactive = true,
  onEdgeClick,
}: CanvasEdgeLayerProps) {
  const nodeMap = new Map<string, CanvasNode>();
  nodes.forEach((n) => nodeMap.set(n.id, n));
  const highlightSet = new Set(highlightedNodeIds);
  const selectedSet = new Set(selectedEdgeIds);

  return (
    <svg
      className="pointer-events-none absolute"
      style={{ left: 0, top: 0, width: '10000px', height: '10000px', overflow: 'visible' }}
    >
      {edges.map((edge) => {
        const source = nodeMap.get(edge.sourceNodeId);
        const target = nodeMap.get(edge.targetNodeId);
        if (!source || !target) return null;

        const sx = source.x + source.width;
        const sy = source.y + source.height / 2;
        const tx = target.x;
        const ty = target.y + target.height / 2;

        const dx = Math.max(40, Math.abs(tx - sx) / 2);
        const d = `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;

        const highlight = highlightSet.has(edge.sourceNodeId) || highlightSet.has(edge.targetNodeId);
        const selected = selectedSet.has(edge.id);
        const lineage = isAgentLineageEdge(edge, nodes);

        let baseColor = '#94a3b8';
        if (source.type === 'generate_image' || source.type === 'generate_video') {
          baseColor = '#10b981';
        } else if (source.type === 'llm') {
          baseColor = '#f59e0b';
        }

        return (
          <g key={edge.id} className={interactive ? 'pointer-events-auto' : undefined}>
            {interactive && (
              <path
                d={d}
                fill="none"
                stroke="transparent"
                strokeWidth={14}
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdgeClick?.(edge, e);
                }}
              />
            )}
            <path
              d={d}
              fill="none"
              stroke={selected ? '#2563eb' : highlight ? '#3b82f6' : baseColor}
              strokeWidth={selected || highlight ? 3 : 2}
              strokeLinecap="round"
              opacity={selected || highlight ? 1 : 0.75}
              strokeDasharray={lineage ? '7 5' : undefined}
            />
            {(selected || highlight) && (
              <path
                d={d}
                fill="none"
                stroke={selected ? '#2563eb' : '#3b82f6'}
                strokeWidth={6}
                strokeLinecap="round"
                opacity={0.12}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
